// helpers
import { log } from 'shared/helpers/newRelic';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

// constants
import {
  ITEM_ERROR_CODES,
  ITEM_DISCOUNT_TYPE,
  ITEM_DISCOUNT_PRIORITY,
  CART_ERROR_CODES,
} from 'shared/constants/cart';
import { ERROR_TAGS } from 'shared/helpers/newRelic/constants';
import { EXPERIMENT_CUSTOMISATION } from './constants';

// defs
import { Cart, CartAddPartial, CartError, CartItemDiscountLabel } from 'shared/types/cart';
import { CartWidgets } from 'shared/types/cartWidgets';

const nullToUndefined = <T>(value: T | null): T | undefined => (value === null ? undefined : value);

const iOOS = (error?: CartError) => {
  return (
    error !== undefined &&
    error.errorCode !== ITEM_ERROR_CODES.NON_SERVICEABLE &&
    error.errorCode !== ITEM_ERROR_CODES.INSUFFICIENT_QTY_IN_STOCK &&
    error.errorCode !== ITEM_ERROR_CODES.MAX_SALEABLE_QTY &&
    error.errorCode !== ITEM_ERROR_CODES.INTERNATIONAL_NOT_AVAILABLE
  );
};

const getCartServiceableError = (error?: CartError) => {
  return (
    error !== undefined &&
    error?.errorCode == CART_ERROR_CODES.NOT_SERVICEABLE &&
    error?.errorMessage
  );
};

const itemTransformer = (item: any) => {
  const { isEligible: isCustomisationEligible } = getEligibleVariantName(EXPERIMENT_CUSTOMISATION);

  const customAttributes = isCustomisationEligible
    ? {
        isCustomizable: item.isCustomizable || 0,
        cartItemId: item?.cartItemId,
        productCustomAttributes: item.productCustomAttributes,
      }
    : {};

  // If any node is there with priority === 3 or 4, it means additional offer exists on that product,
  // and we need to show 'You Pay' strip there.
  const isOfferAvailable = item.cartItemDiscountLabelDTOList?.some(
    (val: CartItemDiscountLabel) =>
      val.priority === ITEM_DISCOUNT_PRIORITY.MRP_DISCOUNT ||
      val.priority === ITEM_DISCOUNT_PRIORITY.NON_MRP_DISCOUNT
  );

  const youPayObj = isOfferAvailable
    ? item.cartItemDiscountLabelDTOList?.find(
        (val: CartItemDiscountLabel) => val.type === ITEM_DISCOUNT_TYPE.YOU_PAY
      )
    : null;

  try {
    return {
      availableQty: item.availableQty,
      cartItemFlagDTO: item.cartItemFlagDTO,
      cartItemPriceDTO: item.cartItemPriceDTO,
      cartItemDiscountLabelDTOList: item.cartItemDiscountLabelDTOList,
      estimatedDeliveryTime: item.estimatedDeliveryTime,
      eddMessage: item.eddMessage,
      imageUrl: item.imageUrl,
      isInternationalAvailable: item.isInternationalAvailable,
      isMadeToOrder: item.isMadeToOrder,
      isPrepaidOnly: item.isPrepaidOnly,
      maxAllowedMsg: item.maxAllowedMsg,
      maxSaleableQty: item.maxSaleableQty,
      name: item.name,
      brandNames: item.brandNames || [],
      brandTitle: item.brandTitle,
      productTitle: item.productTitle,
      freeProductDescription: nullToUndefined(item.offerCommunication),
      offerCommunication: nullToUndefined(item.offerCommunication),
      packSize: nullToUndefined(item.packSize),
      parentId: item.parentId,
      productId: item.productId,
      quantity: item.quantity,
      shade: item.shade,
      sku: item.sku,
      slug: item.slug,
      slugV2: item?.slugV2,
      storePriority: item?.storePriority,
      sizeOptions: nullToUndefined(item.sizeOptions),
      stockMsg: nullToUndefined(item.stockMsg),
      variantIcon: item.variantIcon,
      isOutOfStock: iOOS(nullToUndefined(item.error)),
      hasError: nullToUndefined(item.error) !== undefined,
      error: nullToUndefined(item.error),
      leadTime: item.leadTime || null,
      productType: item?.productType,
      productImages: item?.imageUrls || [],
      youPayPrice: youPayObj ? youPayObj.amount : -1,
      isNonReturnable: item.cartItemFlagDTO?.isNonReturnable,
      isItemNonReturnable: item.cartItemFlagDTO?.isItemNonReturnable,
      isExchangeAllowed: item?.replaceableFlag,
      returnPeriod: item?.returnPeriod,
      priceDropValue: item?.pdMessage,
      exclusionCommunicationDTO: item?.exclusionCommunicationDTO,
      onSale: item?.onSale,
      tag: item?.tag,
      epoch: item?.epoch,
      cartItemId: item?.cartItemId,
      selectedItemForCheckout: true,
      trackingParam: item?.trackingParam,
      vendorDTO: nullToUndefined(item?.vendorDTO),
      catalogTag: item?.catalogTag,
      ...customAttributes,
    };
  } catch (e) {
    log({
      errTitle: 'Error in cart item transformer',
      tags: { module: ERROR_TAGS.TRANFORMER },
      extra: { transformer: 'Cart Item Transformer' },
    });
    throw e;
  }
};

const itemTransformerV2 = (item: any) => {
  const { isEligible: isCustomisationEligible } = getEligibleVariantName(EXPERIMENT_CUSTOMISATION);

  const customAttributes = isCustomisationEligible
    ? {
        isCustomizable: item.isCustomizable || 0,
        cartItemId: item?.cartItemId,
        productCustomAttributes: item.productCustomAttributes,
      }
    : {};

  // If any node is there with priority === 3 or 4, it means additional offer exists on that product,
  // and we need to show 'You Pay' strip there.
  const isOfferAvailable = item.cartItemDiscountLabelDTOList?.some(
    (val: CartItemDiscountLabel) =>
      val.priority === ITEM_DISCOUNT_PRIORITY.MRP_DISCOUNT ||
      val.priority === ITEM_DISCOUNT_PRIORITY.NON_MRP_DISCOUNT
  );

  const youPayObj = isOfferAvailable
    ? item.cartItemDiscountLabelDTOList?.find(
        (val: CartItemDiscountLabel) => val.type === ITEM_DISCOUNT_TYPE.YOU_PAY
      )
    : null;

  try {
    return {
      availableQty: item.availableQty,
      cartItemFlagDTO: item.cartItemFlagDTO,
      cartItemPriceDTO: item.cartItemPriceDTO,
      cartItemDiscountLabelDTOList: item.cartItemDiscountLabelDTOList,
      estimatedDeliveryTime: item.estimatedDeliveryTime,
      eddMessage: item.eddMessage,
      imageUrl: item.imageUrl,
      isInternationalAvailable: item.isInternationalAvailable,
      isMadeToOrder: item.isMadeToOrder,
      isPrepaidOnly: item.isPrepaidOnly,
      maxAllowedMsg: item.maxAllowedMsg,
      maxSaleableQty: item.maxSaleableQty,
      name: item.name,
      brandNames: item.brandNames || [],
      brandTitle: item.brandTitle,
      productTitle: item.productTitle,
      freeProductDescription: nullToUndefined(item.offerCommunication),
      offerCommunication: nullToUndefined(item.offerCommunication),
      packSize: nullToUndefined(item.packSize),
      parentId: item.parentId,
      productId: item.productId,
      quantity: item.quantity,
      shade: item.shade,
      sku: item.sku,
      slug: item.slug,
      slugV2: item?.slugV2,
      storePriority: item?.storePriority,
      sizeOptions: nullToUndefined(item.sizeOptions),
      stockMsg: nullToUndefined(item.stockMsg),
      variantIcon: item.variantIcon,
      isOutOfStock: iOOS(nullToUndefined(item.error)),
      hasError: nullToUndefined(item.error) !== undefined,
      error: nullToUndefined(item.error),
      leadTime: item.leadTime || null,
      productType: item?.productType,
      productImages: item?.imageUrls || [],
      youPayPrice: youPayObj ? youPayObj.amount : -1,
      isNonReturnable: item.cartItemFlagDTO?.isNonReturnable,
      isItemNonReturnable: item.cartItemFlagDTO?.isItemNonReturnable,
      isExchangeAllowed: item?.replaceableFlag,
      returnPeriod: item?.returnPeriod,
      priceDropValue: item?.pdMessage,
      exclusionCommunicationDTO: item?.exclusionCommunicationDTO,
      cartItemId: item?.cartItemId,
      selectedItemForCheckout: false,
      trackingParam: item?.trackingParam,
      vendorDTO: nullToUndefined(item?.vendorDTO),
      catalogTag: item?.catalogTag,
      ...customAttributes,
    };
  } catch (e) {
    log({
      errTitle: 'Error in cart item transformer',
      tags: { module: ERROR_TAGS.TRANFORMER },
      extra: { transformer: 'Cart Item Transformer' },
    });
    throw e;
  }
};

export const transformCart = (cart: any) => {
  try {
    // if no items in cart return undefined
    if ((cart?.items || []).length === 0 && (cart?.unselectedItems || []).length === 0) {
      return undefined;
    }

    const items = cart.items
      .filter(
        (item: any) =>
          item.cartItemFlagDTO.isFreeProduct === false &&
          item.cartItemFlagDTO.isFreeSample === false
      )
      .map(itemTransformer);

    const unselectedItems = cart.unselectedItems
      .filter(
        (item: any) =>
          item.cartItemFlagDTO.isFreeProduct === false &&
          item.cartItemFlagDTO.isFreeSample === false
      )
      .map(itemTransformerV2);

    const freeItems = cart.items
      .filter((item: any) => item.cartItemFlagDTO.isFreeProduct === true)
      .map(itemTransformer);

    const freeSamples = cart.items
      .filter((item: any) => item.cartItemFlagDTO.isFreeSample === true)
      .map(itemTransformer);

    const youPayCount = items.filter((item: any) => item.youPayPrice >= 0).length;

    // for guest user address can be undefined
    const address =
      cart?.addressDTO != null
        ? {
            ...cart.addressDTO,
            error: nullToUndefined(cart.addressDTO?.error),
            isdCode: nullToUndefined(cart.addressDTO?.isdCode),
            regionId: nullToUndefined(cart.addressDTO?.regionId),
          }
        : undefined;

    const infoMessageDTO = {
      infoText: nullToUndefined(cart?.infoMessageDTO?.infoText),
      infoId: nullToUndefined(cart?.infoMessageDTO?.infoId),
      infoTitle: nullToUndefined(cart?.infoMessageDTO?.infoTitle),
      infoUrl: nullToUndefined(cart?.infoMessageDTO?.infoUrl),
    };

    const paymentMethodDTO = {
      ...(cart?.availablePaymentMethodDTO ?? {}),
      codStatus: nullToUndefined(cart?.availablePaymentMethodDTO?.codStatus),
      disabledPaymentMethodMessage: nullToUndefined(
        cart?.availablePaymentMethodDTO?.disabledPaymentMethodMessage
      ),
      offerDTO: nullToUndefined(cart?.availablePaymentMethodDTO?.offerDTO),
      paymentGateway: nullToUndefined(cart?.availablePaymentMethodDTO?.paymentGateway),
    };

    const shippingDataDTO = {
      ...(cart?.shippingDataDTO ?? {}),
      appliedShippingCharge: nullToUndefined(cart?.shippingDataDTO?.appliedShippingCharge),
      appliedShippingDTO: nullToUndefined(cart?.shippingDataDTO?.appliedShippingDTO),
      appliedShippingLimit: nullToUndefined(cart?.shippingDataDTO?.appliedShippingLimit),
      shippingText: nullToUndefined(cart?.shippingDataDTO?.shippingText),
      convenienceChargesDTO: nullToUndefined(
        cart?.shippingDataDTO?.cartConvenienceChargesBreakupDto
      ),
    };

    return {
      items,
      freeItems,
      freeSamples,
      address,
      paymentMethodDTO,
      infoMessageDTO,
      shippingDataDTO,
      cartFlagDTO: cart.cartFlagDTO,
      cartPriceDTO: cart.cartPriceDTO,
      cartTotal: cart.cartTotal,
      countryCode: cart.countryCode,
      currencyCode: cart.currencyCode,
      customerId: cart.customerId,
      domain: cart.domain,
      itemCount: cart.itemCount,
      quantity: cart.quantity,
      appliedOfferLabelDetails: nullToUndefined(cart.appliedOfferLabelDetails),
      couponCode: nullToUndefined(cart.couponCode),
      sizeOptions: nullToUndefined(cart.sizeOptions),
      error: nullToUndefined(cart.error),
      freeGiftsBannerData: nullToUndefined(cart.freeSampleData),
      giftWrapDTO: nullToUndefined(cart.giftWrapDTO),
      giftcard: nullToUndefined(cart.giftcard),
      pakkaPromiseData: nullToUndefined(cart.pakkaPromiseData),
      deltaCommunicationDTO: nullToUndefined(cart.deltaCommunicationDTO),
      rewardsDTO: nullToUndefined(cart.rewardsDTO),
      cartError: nullToUndefined(cart.cartError),
      fomoTagDTO: nullToUndefined(cart.fomoTagDTO),
      youPayCount: youPayCount || 0,
      cartServiceableError: getCartServiceableError(cart?.error),
      pincodeCity: cart.pincodeCity,
      pincode: cart.pincode,
      unselectedItems: unselectedItems,
      paymentMethodOffersDTO: nullToUndefined(cart.paymentMethodOffersDTO),
    } as Cart;
  } catch (e) {
    log({
      errTitle: 'Error in cart transformer',
      tags: { module: ERROR_TAGS.TRANFORMER },
      extra: { transformer: 'Cart Transformer' },
    });
    throw e;
  }
};

export const cartWidgetsTransformer = (widgets: any) => {
  try {
    const relatedProductsList = widgets?.response?.relatedProducts || [];

    if (relatedProductsList.length === 0) {
      return {
        showWidgets: false,
        products: [],
        title: '',
      } as CartWidgets;
    }

    const { products, title } = relatedProductsList[0];

    return {
      showWidgets: true,
      products,
      title,
    } as CartWidgets;
  } catch (e) {
    log({
      errTitle: 'Error in cart widget transformer',
      tags: { module: ERROR_TAGS.TRANFORMER },
      extra: { transformer: 'Cart Widget Transformer' },
    });
    throw e;
  }
};

export const transformAddToCart = (cart: any) => {
  try {
    // if no items in cart return undefined
    if (cart.items.length === 0) {
      return {};
    }

    return {
      cartPriceDTO: cart.cartPriceDTO,
      cartTotal: cart.cartTotal,
      itemCount: cart.itemCount,
      quantity: cart.quantity,
      error: nullToUndefined(cart.error),
      items: cart.items.map(itemTransformer),
    } as CartAddPartial;
  } catch (e) {
    log({
      errTitle: 'Error in transformAddToCart',
      tags: { module: ERROR_TAGS.TRANFORMER },
      extra: { transformer: 'Add to cart Transformer' },
    });
    throw e;
  }
};
